import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  TextField,
  Button,
  Card,
  CardContent,
  MenuItem,
  Stack,
  Alert,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Avatar,
  Tooltip,
  Divider,
  IconButton,
  Slide
} from '@mui/material';
import {
  LocalizationProvider
} from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import { format, parseISO, isBefore, startOfDay } from 'date-fns';
import { ChevronLeft, ChevronRight, CalendarToday } from '@mui/icons-material';
import { CheckCircleOutline } from '@mui/icons-material';
import axios from '../utils/axiosConfig';
import StripePaymentForm from '../components/StripePaymentForm';

const Schedule = () => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [trainingSessions, setTrainingSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [formData, setFormData] = useState({
    parentName: '',
    playerName: '',
    parentEmail: '',
    parentPhone: '',
  });
  const [trainers, setTrainers] = useState([]);
  const [selectedTrainer, setSelectedTrainer] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [loadingTrainers, setLoadingTrainers] = useState(true);
  const [bookingDialogOpen, setBookingDialogOpen] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [bookedSession, setBookedSession] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  useEffect(() => {
    if (!showPayment) {  // Only fetch if not in payment view
      fetchTrainingSessions();
      fetchTrainers();
    }
  }, [selectedDate, selectedTrainer]);

  const fetchTrainingSessions = async () => {
    try {
      setLoading(true);
      setError('');
      let url = '/api/v2/training/sessions/published';
      if (selectedTrainer) {
        url += `?trainer=${selectedTrainer}`;
      }
      const response = await axios.get(url);
      setTrainingSessions(response.data);
    } catch (error) {
      console.error('Error fetching sessions:', error);
      setError('Failed to load training sessions');
    } finally {
      setLoading(false);
    }
  };

  const fetchTrainers = async () => {
    try {
      setLoadingTrainers(true);
      const response = await axios.get('/api/v2/training/trainers');
      if (response.data && Array.isArray(response.data)) {
        setTrainers(response.data.filter(trainer => trainer && trainer.name));
      } else {
        setTrainers([]);
      }
    } catch (error) {
      console.error('Error fetching trainers:', error);
      setError('Failed to load trainers');
    } finally {
      setLoadingTrainers(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
    setSelectedSession(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleTrainerChange = (e) => {
    const value = e.target.value;
    setSelectedTrainer(value === 'all' ? '' : value);
  };

  const handleSessionSelect = (session) => {
    handleBookingOpen(session);
  };

  const handleBookingOpen = (session) => {
    if (!session) return;
    setSelectedSession(session);
    setBookingDialogOpen(true);
    setError('');
    setShowPayment(false);
  };

  const handleBookingClose = () => {
    setBookingDialogOpen(false);
    setSelectedSession(null);
    setFormData({
      parentName: '',
      playerName: '',
      parentEmail: '',
      parentPhone: '',
    });
    setShowPayment(false);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedSession) {
      setError('No session selected');
      return;
    }

    // Check if session is full based on currentParticipants
    if (selectedSession.currentParticipants >= selectedSession.maxParticipants) {
      setError('This session is full');
      return;
    }

    // Validate form
    if (!formData.parentName || !formData.playerName || !formData.parentEmail || !formData.parentPhone) {
      setError('Please fill in all required fields');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.parentEmail)) {
      setError('Please enter a valid email address');
      return;
    }

    // Phone validation - allow various formats
    const phoneRegex = /^[\d\s()-]+$/;
    if (!phoneRegex.test(formData.parentPhone)) {
      setError('Please enter a valid phone number');
      return;
    }

    try {
      setError('');
      setShowPayment(true);
    } catch (err) {
      console.error('Error:', err);
      setError(err.message || 'An error occurred. Please try again.');
    }
  };

  const handlePaymentSuccess = async (paymentIntent) => {
    try {
      console.log('Payment succeeded:', paymentIntent);
      
      // Book the session first
      const bookingResponse = await axios.post(`/api/v2/training/sessions/${selectedSession._id}/book`);
      
      // Only proceed if booking was successful
      if (!bookingResponse.data) {
        throw new Error('Failed to book session');
      }

      // Notify the server about the successful payment
      await axios.post('/api/v2/stripe/payment-success', {
        paymentIntentId: paymentIntent.id,
        sessionId: selectedSession._id,
        customerData: formData
      });

      // Save booked session details for success dialog
      setBookedSession({
        title: selectedSession.title,
        trainer: selectedSession.trainer.name,
        date: format(parseISO(selectedSession.startTime), 'PPPP'),
        time: format(parseISO(selectedSession.startTime), 'p'),
        email: formData.parentEmail
      });

      // Close dialogs and show success
      setBookingDialogOpen(false);
      setSuccessDialogOpen(true);
      
      // Refresh the sessions list
      await fetchTrainingSessions();
    } catch (err) {
      console.error('Error processing payment success:', err);
      setError('Failed to complete booking. Please contact support.');
    }
  };

  const handlePaymentCancel = () => {
    setShowPayment(false);
    setError('');
  };

  const handleBookInPerson = async () => {
    if (!selectedSession) {
      setError('No session selected');
      return;
    }

    // Check if session is full
    if (selectedSession.currentParticipants >= selectedSession.maxParticipants) {
      setError('This session is full');
      return;
    }

    // Validate form fields
    if (!formData.parentName || !formData.playerName || !formData.parentEmail || !formData.parentPhone) {
      setError('Please fill in all required fields');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.parentEmail)) {
      setError('Please enter a valid email address');
      return;
    }

    // Phone validation
    const phoneRegex = /^[\d\s()-]+$/;
    if (!phoneRegex.test(formData.parentPhone)) {
      setError('Please enter a valid phone number');
      return;
    }

    setConfirmDialogOpen(true);
  };

  const handleConfirmBooking = async () => {
    setConfirmDialogOpen(false);
    try {
      setError('');
      const response = await axios.post(`/api/v2/training/sessions/${selectedSession._id}/book-in-person`, {
        parentName: formData.parentName,
        playerName: formData.playerName,
        parentEmail: formData.parentEmail,
        parentPhone: formData.parentPhone
      });

      // Save booked session details for success dialog
      const session = response.data.session;
      setBookedSession({
        title: session.title,
        trainer: session.trainer.name,
        date: format(parseISO(session.startTime), 'PPPP'),
        time: format(parseISO(session.startTime), 'p'),
        email: formData.parentEmail
      });

      // Close booking dialog and show success
      setBookingDialogOpen(false);
      setSuccessDialogOpen(true);

      // Refresh sessions
      await fetchTrainingSessions();
    } catch (err) {
      console.error('Error booking in person:', err);
      setError(err.response?.data?.message || 'Failed to book in person. Please try again.');
    }
  };

  const handleCancelConfirm = () => {
    setConfirmDialogOpen(false);
  };

  const filterSessionsByDate = (sessions) => {
    return sessions.filter(session => 
      format(parseISO(session.startTime), 'yyyy-MM-dd') === format(selectedDate, 'yyyy-MM-dd')
    );
  };

  const findNextAvailableSession = () => {
    if (!trainingSessions.length) return null;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    return trainingSessions
      .filter(session => {
        const sessionDate = new Date(session.startTime);
        return sessionDate > selectedDate && session.currentParticipants < session.maxParticipants;
      })
      .sort((a, b) => new Date(a.startTime) - new Date(b.startTime))[0];
  };

  const handleNextSessionClick = (nextSession) => {
    const nextDate = new Date(nextSession.startTime);
    setSelectedDate(nextDate);
  };

  const renderNoSessionsMessage = () => {
    const nextSession = findNextAvailableSession();
    
    return (
      <Paper 
        sx={{ 
          p: 2,
          textAlign: 'center',
          bgcolor: 'grey.50',
          border: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Typography variant="body2" color="text.secondary">
          No sessions available for {format(selectedDate, 'MMMM d')}
          {nextSession && (
            <Box 
              component="span"
              sx={{ 
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                ml: 2,
                p: 0.75,
                px: 1.5,
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: 'action.hover'
                }
              }}
              onClick={() => handleNextSessionClick(nextSession)}
            >
              <Typography variant="body2" color="text.secondary">
                Next:
              </Typography>
              <Typography variant="body2" color="primary" fontWeight="medium">
                {format(parseISO(nextSession.startTime), 'MMM d')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                •
              </Typography>
              <Typography variant="body2">
                {format(parseISO(nextSession.startTime), 'h:mm a')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                •
              </Typography>
              <Typography variant="body2">
                ${nextSession.price}
              </Typography>
            </Box>
          )}
        </Typography>
      </Paper>
    );
  };

  const renderSessions = () => {
    const filteredSessions = filterSessionsByDate(trainingSessions);
    
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      );
    }

    if (filteredSessions.length === 0) {
      return renderNoSessionsMessage();
    }

    return (
      <Box sx={{ 
        maxHeight: '500px', 
        overflowY: 'auto',
        '&::-webkit-scrollbar': {
          width: '8px',
        },
        '&::-webkit-scrollbar-track': {
          background: '#f1f1f1',
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#888',
          borderRadius: '4px',
          '&:hover': {
            background: '#555',
          },
        },
      }}>
        {filteredSessions.map((session) => (
          <Paper 
            key={session._id}
            elevation={1}
            sx={{ 
              py: 1, 
              px: 1.5, 
              mb: 1, 
              opacity: session.currentParticipants >= session.maxParticipants ? 0.7 : 1,
              cursor: 'pointer',
              '&:hover': {
                bgcolor: 'action.hover'
              }
            }}
            onClick={() => handleSessionSelect(session)}
          >
            <Grid container spacing={1} alignItems="flex-start">
              <Grid item xs={12} sm={2}>
                <Typography variant="body1" color="primary" fontWeight="bold">
                  {format(parseISO(session.startTime), 'h:mm a')}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Stack spacing={0.5}>
                  <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
                    {session.title}
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Avatar sx={{ width: 20, height: 20, bgcolor: 'primary.main' }}>
                      {session.trainer?.name?.charAt(0) || '?'}
                    </Avatar>
                    <Typography variant="body2" color="text.secondary">
                      {session.trainer?.name || 'No trainer assigned'}
                      {session.trainer?.title && ` (${session.trainer.title})`}
                    </Typography>
                  </Stack>
                </Stack>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Grid container spacing={1}>
                  <Grid item xs={4}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.25 }}>
                        Duration
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {session.duration} mins
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.25 }}>
                        Location
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {session.location}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.25 }}>
                        Age Group
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {session.ageGroup}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.25 }}>
                        Spots Left
                      </Typography>
                      <Typography 
                        variant="body2" 
                        fontWeight="medium"
                        color={session.currentParticipants >= session.maxParticipants ? "error.main" : "success.main"}
                      >
                        {session.maxParticipants - session.currentParticipants}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} sm={2}>
                <Button 
                  variant="contained" 
                  color="primary" 
                  fullWidth
                  sx={{
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 'bold',
                    bgcolor: session.currentParticipants >= session.maxParticipants ? 'grey.500' : 'primary.main',
                    '&:hover': {
                      bgcolor: session.currentParticipants >= session.maxParticipants ? 'grey.600' : 'primary.dark',
                    }
                  }}
                  disabled={session.currentParticipants >= session.maxParticipants}
                >
                  {session.currentParticipants >= session.maxParticipants ? 'Full' : `$${session.price}`}
                </Button>
              </Grid>
            </Grid>
            {session.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                {session.description}
              </Typography>
            )}
          </Paper>
        ))}
      </Box>
    );
  };

  const renderBookingDialog = () => {
    return (
      <Dialog
        open={bookingDialogOpen}
        onClose={handleBookingClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {showPayment ? 'Payment Details' : 'Book Training Session'}
        </DialogTitle>
        <DialogContent>
          {!showPayment && selectedSession && (
            <Box sx={{ mb: 3, p: 3, bgcolor: 'background.paper', borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'primary.main', mb: 2 }}>
                {selectedSession.title}
              </Typography>
              <Grid container spacing={3} sx={{ mt: 1 }}>
                <Grid item xs={6} sm={3}>
                  <Typography variant="subtitle2" sx={{ 
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    mb: 0.5
                  }}>
                    Duration
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                    {selectedSession.duration} mins
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="subtitle2" sx={{ 
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    mb: 0.5
                  }}>
                    Location
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                    {selectedSession.location}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="subtitle2" sx={{ 
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    mb: 0.5
                  }}>
                    Age Group
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                    {selectedSession.ageGroup}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="subtitle2" sx={{ 
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    mb: 0.5
                  }}>
                    Price
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                    ${selectedSession.price}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="subtitle2" sx={{ 
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    mb: 0.5
                  }}>
                    Spots Left
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                    {selectedSession.maxParticipants - selectedSession.currentParticipants}
                  </Typography>
                </Grid>
              </Grid>
              {selectedSession.description && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" sx={{ 
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    mb: 0.5
                  }}>
                    Description
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'text.primary' }}>
                    {selectedSession.description}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {!showPayment ? (
            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    label="Parent/Guardian Name"
                    name="parentName"
                    value={formData.parentName}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    label="Player Name"
                    name="playerName"
                    value={formData.playerName}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    label="Parent/Guardian Email"
                    name="parentEmail"
                    type="email"
                    value={formData.parentEmail}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    label="Parent/Guardian Phone"
                    name="parentPhone"
                    value={formData.parentPhone}
                    onChange={handleChange}
                  />
                </Grid>
              </Grid>
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button onClick={handleBookingClose}>
                  Cancel
                </Button>
                <Button
                  onClick={handleBookInPerson}
                  variant="outlined"
                  color="secondary"
                >
                  Book In Person
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                >
                  Book Online
                </Button>
              </Box>
            </Box>
          ) : (
            <StripePaymentForm
              onClose={() => {
                setShowPayment(false);
                setBookingDialogOpen(false);
              }}
              sessionId={selectedSession._id}
              sessionPrice={selectedSession.price}
              playerName={formData.playerName}
              parentName={formData.parentName}
              parentEmail={formData.parentEmail}
              parentPhone={formData.parentPhone}
              onPaymentSuccess={handlePaymentSuccess}
            />
          )}
        </DialogContent>
      </Dialog>
    );
  };

  const renderSuccessDialog = () => {
    return (
      <Dialog
        open={successDialogOpen}
        onClose={() => setSuccessDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ textAlign: 'center' }}>
          Booking Confirmed!
        </DialogTitle>
        <DialogContent>
          {bookedSession && (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <CheckCircleOutline sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                {bookedSession.title}
              </Typography>
              <Typography variant="body1" gutterBottom>
                with {bookedSession.trainer}
              </Typography>
              <Typography variant="body1" gutterBottom>
                {bookedSession.date} at {bookedSession.time}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                A confirmation email has been sent to {bookedSession.email}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button
            onClick={() => setSuccessDialogOpen(false)}
            variant="contained"
            color="primary"
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const renderConfirmDialog = () => {
    return (
      <Dialog
        open={confirmDialogOpen}
        onClose={handleCancelConfirm}
        aria-labelledby="confirm-booking-dialog"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="confirm-booking-dialog" sx={{ pb: 1 }}>
          Confirm In-Person Booking
        </DialogTitle>
        <DialogContent sx={{ pb: 2 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            You are about to book a training session with payment due at the session.
          </Typography>
          <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Session Price
                </Typography>
                <Typography variant="h6" color="primary">
                  ${selectedSession?.price.toFixed(2)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Payment Method
                </Typography>
                <Typography variant="body1">
                  Due at Session
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCancelConfirm} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleConfirmBooking} variant="contained" color="primary">
            Confirm Booking
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const renderTrainerSelect = () => (
    <Paper sx={{ p: 2, border: '1px solid', borderColor: 'divider' }}>
      <Typography variant="subtitle1" gutterBottom sx={{ color: 'text.primary' }}>
        Filter by Trainer
      </Typography>
      <TextField
        select
        size="small"
        value={selectedTrainer || 'all'}
        onChange={handleTrainerChange}
        fullWidth
        disabled={loadingTrainers}
        sx={{
          '& .MuiSelect-select': {
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            py: 1
          }
        }}
      >
        <MenuItem value="all">
          <Typography>All Trainers</Typography>
        </MenuItem>
        {trainers.map((trainer) => (
          <MenuItem key={trainer._id} value={trainer._id}>
            <Stack direction="row" spacing={1} alignItems="center" width="100%">
              <Avatar sx={{ width: 24, height: 24, bgcolor: 'primary.main' }}>
                {trainer.name.charAt(0)}
              </Avatar>
              <Box>
                <Typography>{trainer.name}</Typography>
                {trainer.title && (
                  <Typography variant="caption" color="text.secondary">
                    {trainer.title}
                  </Typography>
                )}
              </Box>
            </Stack>
          </MenuItem>
        ))}
      </TextField>
      {loadingTrainers && (
        <Box display="flex" justifyContent="center" mt={1}>
          <CircularProgress size={20} />
        </Box>
      )}
    </Paper>
  );

  const CustomPickersDay = (props) => {
    const { day, ...other } = props;
    const today = startOfDay(new Date());
    
    let sx = {};
    
    // Past dates are dark grey
    if (isBefore(day, today)) {
      sx = { bgcolor: 'rgba(158, 158, 158, 0.2)' };
    } else {
      // Check if there are sessions on this date
      const dateStr = format(day, 'yyyy-MM-dd');
      const sessionsOnDay = trainingSessions.filter(session => 
        format(parseISO(session.startTime), 'yyyy-MM-dd') === dateStr
      );

      if (sessionsOnDay.length > 0) {
        sx = { bgcolor: 'rgba(76, 175, 80, 0.25)' }; // Increased green opacity from 0.1 to 0.25
      } else {
        sx = { bgcolor: 'rgba(238, 238, 238, 0.3)' }; // Light grey
      }
    }

    return <PickersDay {...other} day={day} sx={sx} />;
  };

  return (
    <Box sx={{ mt: '64px' }}>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ color: 'text.primary' }}>
          Training Schedule
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          Book your next training session with our professional coaches
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            {renderTrainerSelect()}
            <Paper 
              sx={{ 
                p: 2,
                mt: 2,
                border: '1px solid',
                borderColor: 'divider'
              }}
            >
              <Typography variant="subtitle1" gutterBottom sx={{ color: 'text.primary' }}>
                Select Date
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateCalendar 
                  value={selectedDate}
                  onChange={setSelectedDate}
                  slots={{
                    day: CustomPickersDay
                  }}
                />
              </LocalizationProvider>
            </Paper>
          </Grid>

          <Grid item xs={12} md={8}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ color: 'text.primary' }} fontWeight="medium">
                Available Sessions for {format(selectedDate, 'MMMM d, yyyy')}
              </Typography>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                  <CircularProgress />
                </Box>
              ) : error ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              ) : filterSessionsByDate(trainingSessions).length === 0 ? (
                renderNoSessionsMessage()
              ) : (
                renderSessions()
              )}
            </Box>
          </Grid>
        </Grid>
        {renderBookingDialog()}
        {renderSuccessDialog()}
        {renderConfirmDialog()}
      </Container>
    </Box>
  );
};

export default Schedule;
